import { Component, ViewChild } from '@angular/core';
import * as dayjs from 'dayjs';
import { PushToDeviceService } from 'src/app/services/push-to-device.service';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { CommonService } from 'src/app/services/common.service';
import { SiteService } from '../../services/site.service';
import { OTA_Status } from '../../enums/SequenceKeys.enum';
import { MatDialogRef } from '@angular/material/dialog';
import { ObjectUtil } from '../../utils/object-util';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

@Component({
  selector: 'app-delivery-debug-info-modal',
  templateUrl: './delivery-debug-info-modal.component.html',
  styleUrls: ['./delivery-debug-info-modal.component.scss'],
  standalone: false
})
export class DeliveryDebugInfoModalComponent {

  isLoading: boolean = false;
  isDeliveryLogsPopup: boolean = false;
  deliveryLogs: any = {};
  sequenceId: string = '';
  sequenceName: string = '';
  siteId: string = '';
  appliedDate: string = '';
  siteTimezone: string = '';
  deliveryStatus: any = {};

  //Mat-table
  dataSource!: MatTableDataSource<any>;
  headerColumnNames: string[] = [];
  sortableColumns: string[] = [];
  columnConfig = {
    deliveryLogs: {
      headerColumnNames: ['status', 'ccuZoneModules', 'deliveryDateTime', 'confirmedReceipt'],
      sortableColumns: ['status', 'ccuZoneModules']
    },
    debugInfo: {
      headerColumnNames: ['status', 'ccuZoneModules', 'debugInformation'],
      sortableColumns: ['status', 'ccuZoneModules']
    }
  };
  displayedColumns!: string[];
  tableData: any;
  originalTableData: any[] = [];
  showDebugTooltip: boolean = false;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;


  constructor(public pushToDeviceService: PushToDeviceService,
    public commonService: CommonService,
    public siteService: SiteService,
    public dialogRef: MatDialogRef<DeliveryDebugInfoModalComponent>
  ) { }

  async ngOnInit() {
    this.isLoading = true;
    await this.getSiteInfo(this.siteId);
    this.headerColumnNames = this.isDeliveryLogsPopup ? this.columnConfig.deliveryLogs.headerColumnNames : 
      this.columnConfig.debugInfo.headerColumnNames;
    this.sortableColumns = this.isDeliveryLogsPopup ? this.columnConfig.deliveryLogs.sortableColumns :
      this.columnConfig.debugInfo.sortableColumns;
    this.displayedColumns = this.headerColumnNames;
    this.dataSource = new MatTableDataSource();

    this.setInitialTableData();
    if (this.isDeliveryLogsPopup) {
      this.getDeliveryLogs();
    } else {
      this.getDebugInfo();
    }
  }

  // set initial table data
  setInitialTableData() {
    this.tableData = [];
    this.originalTableData = this.tableData ? [...this.tableData] : [];
    this.dataSource.data = this.tableData;
    this.dataSource.sort = this.sort;
  }

  // API call to get delivery logs
  getDeliveryLogs() {
    this.isLoading = true;
    this.pushToDeviceService.getSequenceDeliveryLogs(this.sequenceId, this.siteId).subscribe((response: any) => {
      if (response && response?.ccuList && response.ccuList?.length > 0) {
        this.deliveryLogs = response;
        this.appliedDate = this.convertToSiteTimeZone(this.deliveryLogs.ccuList[0].publishedDateTime);
        this.updateTableData(this.deliveryLogs.ccuList);
        this.dataSource.data = this.tableData;
        this.originalTableData = this.tableData ? [...this.tableData] : [];
        this.deriveDeliveryStatus(this.deliveryLogs.ccuList);
      }
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
      this.dialogRef.close();
      this.commonService.showToast('error', error?.error?.error ?? 'Something went wrong. Please try again.');
    });
  }

  getDebugInfo() {
    this.isLoading = true;
    this.pushToDeviceService.getSequenceDebugInfo(this.sequenceId).subscribe((response: any) => {
      if (response && response?.ccuList && response.ccuList?.length > 0) {
        this.deliveryLogs = response;
        if(!this.deliveryLogs?.appliedDate?.includes('Z') && !this.deliveryLogs?.appliedDate?.includes('z')) {
          this.deliveryLogs.appliedDate += 'Z';
        }
        this.appliedDate = this.convertToSiteTimeZone(this.deliveryLogs.appliedDate);
        this.updateTableData(this.deliveryLogs.ccuList);
        this.dataSource.data = this.tableData;
        this.originalTableData = this.tableData ? [...this.tableData] : [];
      }
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
      this.dialogRef.close();
      this.commonService.showToast('error', error?.error?.error ?? 'Something went wrong. Please try again.');
    });
  }

  // deriving the global status of the delivery logs
  deriveDeliveryStatus(CCUList: any[]) {
    const result = CCUList.map(ccu => {
      const otaStatus: string[] = [];
      // CCU OTA Status
      if (ccu.otaStatus) {
        otaStatus.push(ccu.otaStatus);
      }
      // Device OTA Status
      this.getHierarchyGroups(ccu).forEach((group: any) =>
        group.devices?.forEach((device: any) => {
          if (device?.otaStatus) {
            otaStatus.push(device.otaStatus);
          }
        })
      );
      const hasInProgress = otaStatus.includes(OTA_Status.OTA_IN_PROGRESS);
      const hasError = otaStatus.includes(OTA_Status.OTA_ERROR);
      const isAllDelivered = otaStatus?.length > 0 &&
        otaStatus.every(status =>
          status === OTA_Status.OTA_Delivered || status === OTA_Status.OTA_RECIEVED
        );
      return { hasInProgress, hasError, isAllDelivered, ccuRef: ccu.ccuRef };
    });

    const total = result.length;
    const totalError = result.filter(r => r.hasError).length;
    const totalInProgress = result.filter(r => r.hasInProgress).length;
    const totalDelivered = result.filter(r => r.isAllDelivered).length;

    let statusConfig = {};

    if (totalError > 0) {
      statusConfig = {
        status: 'Error',
        tooltip: OTA_Status.OTA_ERROR,
        path: this.getStatusIconPath(OTA_Status.OTA_ERROR)
      };
    } else if (totalDelivered === total) {
      statusConfig = {
        status: 'Complete',
        tooltip: OTA_Status.OTA_Delivered,
        path: this.getStatusIconPath(OTA_Status.OTA_Delivered)
      };
    } else if (totalInProgress > 0) {
      statusConfig = {
        status: `Delivering (${total - totalInProgress}/${total})`,
        tooltip: OTA_Status.OTA_IN_PROGRESS,
        path: this.getStatusIconPath(OTA_Status.OTA_IN_PROGRESS)
      };
    }
    this.deliveryStatus = statusConfig;
  }

  // Helper function to get the actual column name
  getFormattedColumnName(columnName: string) {
    return this.commonService.getColumnNameForCCUDeliveryLogs(columnName);
  }

  // Helper function to get the column width
  getColumnWidth(columnName: string) {
    switch (columnName) {
      case 'status':
        return 'width-10';
      case 'ccuZoneModules':
        return this.isDeliveryLogsPopup ? 'width-38' : 'width-60';
      case 'deliveryDateTime':
        return 'width-26';
      case 'confirmedReceipt':
        return 'width-26';
      case 'debugInformation':
        return 'width-30';
      default:
        return 'auto';
    }
  }

  // Updates the raw API data to the table data
  updateTableData(ccuData: any[]) {
    ccuData.forEach(ccu => {
      this.tableData.push({
        status: ccu.otaStatus,
        ccuZoneModules: `${ccu.ccuName ?? ''}`,
        deliveryDateTime: this.convertToSiteTimeZone(ccu.deliveredDateTime),
        confirmedReceipt: this.convertToSiteTimeZone(ccu.confirmedDateTime),
        isChild: false,
        ccuRef: ccu.ccuRef ?? '',
      });
      this.getHierarchyGroups(ccu).forEach((group: any) => {
        group.devices?.forEach((device: any) => {
          this.tableData.push({
            status: this.commonService.getDeviceOtaStatus(device.otaStatus, device.deviceName) ?? '',
            ccuZoneModules: this.formatDeviceName(this.getGroupName(group, ccu), device.deviceName, device.address),
            deliveryDateTime: this.isDeliveryLogsPopup ? this.convertToSiteTimeZone(device.deliveredDateTime) : '-',
            confirmedReceipt: this.isDeliveryLogsPopup ? this.convertToSiteTimeZone(device.confirmedDateTime) : '-',
            debugInformation: this.isDeliveryLogsPopup ? '-' : this.formatDebugInfo(device.runCount, device.lastRunTimeMs, device.longestRunTimeMs), 
            ccuRef: ccu.ccuRef ?? '',
            ccuName: ccu.ccuName ?? '',
            roomRef: group.roomRef ?? '',
            roomName: group.roomName ?? '',
            systemManagerEquipRef: group.systemManagerEquipRef ?? '',
            systemManagerEquipName: group.systemManagerEquipName ?? '',
            isChild: true
          });
        });
      });
    });
  }

  getHierarchyGroups(ccu: any): any[] {
    return [
      ...(ccu?.systemManagerEquips || []),
      ...(ccu?.rooms || [])
    ];
  }

  getGroupName(group: any, ccu?: any): string {
    if (group?.systemManagerEquipRef || group?.systemManagerEquipName) {
      return 'System Manager';
    }
    return group?.roomName || '';
  }

  // Helper function to get the status icon path
  getStatusIconPath(status: string) {
    return this.commonService.getStatusIconPath(status);
  }

  // Helper function to format the device name
  formatDeviceName(roomName: string, deviceName: string, address: string) {
    const isConnectModule = deviceName
      .toLowerCase()
      .split('-')
      .map(p => p.trim())
      .includes('connectnodedevice');

    const ispcn = deviceName
      .toLowerCase()
      .split('-')
      .map(p => p.trim())
      .includes('pcndevice');
      
    const device = isConnectModule ? 'Connect Node' : (ispcn ? 'Smart Node - Custom Code' : deviceName)
    return `${roomName} - ${device} (${address})`;
  }

  formatDebugInfo(runCount: any, lastRunTimeMs: any, longestRunTimeMs: any) {
    if (!runCount && !lastRunTimeMs && !longestRunTimeMs) return '-';
    const format = (value: any) => ObjectUtil.isNotUndefinedOrNull(value) ? (isNaN(Math.floor(value)) ? value : Math.floor(value)) : '-';
    return `${format(runCount)}/${format(lastRunTimeMs)}/${format(longestRunTimeMs)}`;
  }

  // Helper function to set the sorting option for the specific column
  isSortable(column: string): boolean {
    return this.sortableColumns.includes(column);
  }

  // Handles the sorting of the table data
  onSortChange({ active, direction }: Sort) {
    if (active === '' || direction === '') {
      this.tableData = [...this.originalTableData];
    } else {
      const groupMap = new Map<string, any[]>();
      this.tableData.forEach((row: any) => {
        const key = row.ccuRef ?? row.ccuZoneModules;
        if (!groupMap.has(key)) {
          groupMap.set(key, []);
        }
        groupMap.get(key)!.push(row);
      });

      // Convert to array and sort using parent row
      const groupedArray = Array.from(groupMap.values());
      groupedArray.sort((a, b) => {
        const parentA = a.find(r => !r.isChild);
        const parentB = b.find(r => !r.isChild);
        const valA = parentA?.[active] ?? '';
        const valB = parentB?.[active] ?? '';
        const compare = String(valA).localeCompare(String(valB));
        return direction === 'asc' ? compare : -compare;
      });

      this.tableData = groupedArray.flatMap(group => {
        const parent = group.find(r => !r.isChild);
        const children = group.filter(r => r.isChild);
        return parent ? [parent, ...children] : group;
      });
    }
    this.dataSource.data = this.tableData;
  }

  // Fetches the site information
  async getSiteInfo(siteRef: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.siteService.getReadById(siteRef).subscribe({
        next: ({ rows }) => {
          if (rows.length && rows[0]?.tz) {
            const timezone = rows[0].tz;
            this.siteTimezone = this.siteService.getIANATimeZone(timezone);
          }
          resolve();
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  // converts the API date informations to the site timezone date with the format
  convertToSiteTimeZone(date: string) {
    if (!date || !this.siteTimezone) {
      return '-';
    }
    const convertedDate = dayjs(date.split(' ')[0]);
    if (!convertedDate.isValid()) {
      return '-';
    }
    return dayjs(convertedDate).tz(this.siteTimezone).format('MMMM, Do YYYY | hh:mm:ss A');
  }

}
